import { Modal, Radio, Tag } from 'antd'
import Table, { ColumnGroupType, ColumnsType, ColumnType } from 'antd/es/table'
import CheckableTag from 'antd/es/tag/CheckableTag'
import { defaultSortedColumn } from 'features/fde'
import { Sub } from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  FfsParameterField,
  FfsParameterInputFieldProps,
} from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { useGeckoLocale } from 'features/locale'
import find from 'lodash/find'
import isNil from 'lodash/isNil'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { MinStrengthRec, minStrengthRecords } from './allowableStressValues'

const TableFooterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: right;
`

const LABEL_YIELD = (
  <>
    S<Sub>y</Sub>
  </>
)
const LABEL_TENSILE = (
  <>
    S<Sub>t</Sub>
  </>
)

export interface MinTensileStrengthTableProps {
  stressValue: GeckoMeasurement | null
  setStressValue: Dispatch<SetStateAction<GeckoMeasurement | null>>
  tensileStrength?: GeckoMeasurement | null
  setTensileStrength?: Dispatch<SetStateAction<GeckoMeasurement | null>>
}

function formatStrength(
  value: GeckoMeasurement | null,
  unit: GeckoMeasurementUnit
): string {
  return (
    value
      ?.convertTo(unit)
      .num.toFixed(unit === GeckoMeasurementUnit.Megapascals ? 2 : 0) ?? ''
  )
}

type MinStrengthRowType = MinStrengthRec & { id: string }
const MIN_STRENGTH_RECS: readonly MinStrengthRowType[] = minStrengthRecords.map(
  (rec) => ({
    ...rec,
    id: [rec.material, rec.spec, rec.grade ?? 'null'].join('~'),
  })
)
const DEFAULT_MIN_STRENGTH_ROW_ID =
  find(
    MIN_STRENGTH_RECS,
    (rec) =>
      rec.material === 'A516 Gr. 60' && rec.spec === 'A106' && rec.grade === 'B'
  )?.id ?? null

export const DEFAULT_STRESS_VALUE =
  MIN_STRENGTH_RECS.find((rec) => rec.id === DEFAULT_MIN_STRENGTH_ROW_ID)
    ?.yield[100] ?? null

export const DEFAULT_TENSILTE_STRENGTH =
  MIN_STRENGTH_RECS.find((rec) => rec.id === DEFAULT_MIN_STRENGTH_ROW_ID)
    ?.tensile ?? null

const RawYieldStrengthTag: FC<{
  value: GeckoMeasurement
  selected: boolean
  select: () => void
  unit: GeckoMeasurementUnit
}> = ({ value, selected, select, unit }) => {
  return (
    <Tag.CheckableTag
      checked={selected}
      onChange={(checked) => {
        if (checked) {
          select()
        }
      }}>
      {formatStrength(value, unit)}
    </Tag.CheckableTag>
  )
}
RawYieldStrengthTag.displayName = 'YieldStrengthTag'
const YieldStrengthTag = React.memo(RawYieldStrengthTag)

const PRESSURE_UNIT_OPTIONS = [
  GeckoMeasurementUnit.Megapascals,
  GeckoMeasurementUnit.PoundsForcePerSqInch,
].map((unit) => ({
  label: unit.name,
  value: unit.id,
}))

export const MinTensileStrengthTable: FC<MinTensileStrengthTableProps> = ({
  stressValue,
  setStressValue,
  tensileStrength,
  setTensileStrength,
}) => {
  const [selectedRowId, setSelectedRowId] = useState<string | null>(
    DEFAULT_MIN_STRENGTH_ROW_ID
  )
  const [selectedYieldTemp, setSelectedYieldTemp] = useState<
    keyof MinStrengthRec['yield'] | null
  >(100)

  const [strengthUnit, setStrengthnit] = useState<GeckoMeasurementUnit>(
    GeckoMeasurementUnit.Megapascals
  )
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const preferredStrengthUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Megapascals
  )
  useEffect(
    () => setStrengthnit(preferredStrengthUnit),
    [preferredStrengthUnit]
  )
  const createYieldStrengthChildCol: (
    yieldTemp: keyof MinStrengthRowType['yield']
  ) => ColumnType<MinStrengthRowType> = useCallback(
    (yieldTemp: keyof MinStrengthRowType['yield']) => {
      return {
        title: yieldTemp,
        dataIndex: ['yield', yieldTemp],
        render: (value: GeckoMeasurement, rec) => {
          const selected =
            rec.id === selectedRowId && selectedYieldTemp === yieldTemp
          return (
            <YieldStrengthTag
              unit={strengthUnit}
              value={value}
              selected={selected}
              select={() => {
                setSelectedRowId(rec.id)
                setSelectedYieldTemp(yieldTemp)
              }}
            />
          )
        },
      }
    },
    [
      selectedRowId,
      selectedYieldTemp,
      setSelectedRowId,
      setSelectedYieldTemp,
      strengthUnit,
    ]
  )

  const selectedTableValue = useSelectedYieldStrength({
    selectedRowId,
    selectedYieldTemp,
  })
  const selectedTensileStrength = useSelectedTensileStrength(selectedRowId)

  // If we select a new stress value, make sure to call the setter
  useEffect(() => {
    if (
      selectedTableValue &&
      !GeckoMeasurement.Equals(
        stressValue ?? undefined,
        selectedTableValue ?? undefined
      )
    ) {
      setStressValue(selectedTableValue)
    }
    if (
      selectedTensileStrength &&
      !GeckoMeasurement.Equals(
        tensileStrength ?? undefined,
        selectedTensileStrength ?? undefined
      )
    ) {
      setTensileStrength?.(selectedTensileStrength)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableValue, selectedTensileStrength, setStressValue])

  // If the stress value is changed externally and it no longer
  // matches, just clear the table selection
  useEffect(() => {
    if (
      !GeckoMeasurement.Equals(
        stressValue ?? undefined,
        selectedTableValue ?? undefined
      )
    ) {
      setSelectedRowId(null)
      setSelectedYieldTemp(null)
    }

    if (
      !GeckoMeasurement.Equals(
        tensileStrength ?? undefined,
        selectedTensileStrength ?? undefined
      )
    ) {
      setTensileStrength?.(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stressValue, tensileStrength])

  const columns: ColumnsType<MinStrengthRowType> = useMemo(
    () => [
      defaultSortedColumn('Material', 'material'),
      defaultSortedColumn('Spec. No.', 'spec'),
      defaultSortedColumn('Type / Grade', 'grade'),
      defaultSortedColumn(
        'Tensile Strength',
        'tensile',
        (value: GeckoMeasurement | null, row) =>
          tensileStrength ? (
            <CheckableTag
              checked={
                selectedRowId === row.id &&
                tensileStrength.toNumber(GeckoMeasurementUnit.Megapascals) ===
                  row.tensile.toNumber(GeckoMeasurementUnit.Megapascals)
              }
              onChange={(checked: boolean) => {
                if (checked && setTensileStrength) {
                  setTensileStrength(row.tensile)
                  setSelectedRowId(row.id)
                }
              }}>
              {formatStrength(value, strengthUnit)}
            </CheckableTag>
          ) : (
            formatStrength(value, strengthUnit)
          )
      ),
      {
        title: 'Yield strength @ min temp (Click one)',
        children: [
          createYieldStrengthChildCol(0),
          createYieldStrengthChildCol(100),
          createYieldStrengthChildCol(200),
          createYieldStrengthChildCol(300),
        ],
      } as ColumnGroupType<MinStrengthRowType>,
    ],
    [
      createYieldStrengthChildCol,
      selectedRowId,
      setTensileStrength,
      strengthUnit,
      tensileStrength,
    ]
  )

  return (
    <Table
      columns={columns}
      dataSource={MIN_STRENGTH_RECS}
      rowKey={(rec) => rec.id}
      pagination={false}
      scroll={{ y: '40rem' }}
      footer={() => (
        <TableFooterContainer>
          Unit:{' '}
          <Radio.Group
            options={PRESSURE_UNIT_OPTIONS}
            value={strengthUnit.id}
            onChange={(e) => {
              setStrengthnit(
                GeckoMeasurementUnit.GetByID(e.target.value) ??
                  GeckoMeasurementUnit.Megapascals
              )
            }}
            buttonStyle='solid'
            optionType='button'
            size='small'
          />
        </TableFooterContainer>
      )}
    />
  )
}

type YieldStrengthTableSelection = {
  selectedRowId: string | null
  selectedYieldTemp: keyof MinStrengthRec['yield'] | null
}

export function useSelectedYieldStrength(
  selection: YieldStrengthTableSelection | null
): GeckoMeasurement | null {
  const selectedRowId = selection?.selectedRowId ?? null
  const selectedYieldTemp = selection?.selectedYieldTemp ?? null
  const selRow = useMemo(
    () => find(MIN_STRENGTH_RECS, (rec) => rec.id === selectedRowId),
    [selectedRowId]
  )
  return useMemo(
    () =>
      (!isNil(selectedYieldTemp) ? selRow?.yield[selectedYieldTemp] : null) ??
      null,
    [selectedYieldTemp, selRow?.yield]
  )
}

function useSelectedTensileStrength(
  selectedRowId: string | null
): GeckoMeasurement | null {
  const selRow = useMemo(
    () => find(MIN_STRENGTH_RECS, (rec) => rec.id === selectedRowId),
    [selectedRowId]
  )
  return useMemo(() => (!isNil(selRow) ? selRow.tensile : null), [selRow])
}

export const YieldStrengthParameterField: FC<
  MinTensileStrengthTableProps & Pick<FfsParameterInputFieldProps, 'labelWidth'>
> = ({
  stressValue,
  setStressValue,
  tensileStrength,
  setTensileStrength,
  labelWidth,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  return (
    <>
      <Modal
        title='Pick a Yield Strength'
        forceRender={true}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        footer={<></>}
        open={open}
        width={1000}>
        <MinTensileStrengthTable
          stressValue={stressValue}
          setStressValue={setStressValue}
          tensileStrength={tensileStrength}
          setTensileStrength={setTensileStrength}
        />
      </Modal>
      <FfsParameterField
        label={LABEL_YIELD}
        labelTooltip='Stress Value'
        measurementType='pressure'
        labelWidth={labelWidth}
        value={stressValue}
        setValue={setStressValue}
        onLookup={() => setOpen(true)}
      />
      {tensileStrength !== undefined ? (
        <FfsParameterField
          label={LABEL_TENSILE}
          labelTooltip='Tensile Strength'
          measurementType='pressure'
          labelWidth={labelWidth}
          value={tensileStrength}
          setValue={setTensileStrength}
          onLookup={() => setOpen(true)}
        />
      ) : undefined}
    </>
  )
}
